import type { ICurrentRouteInfo, CandidateRouteInfo, IRoutingMiddleware } from '@wix/thunderbolt-symbols'
import type { PageDefinition } from '@wix/thunderbolt-becky-types'

export const getPageBackgroundId = (pageId: string) => `pageBackground_${pageId}`

const MASTER_PAGE_ID = 'masterPage'

/**
 * Page data type for page utility functions.
 * Accepts either full PageDefinition['data'] or a partial object with the required fields.
 */
export type PageData = Pick<PageDefinition['data'], 'id'> &
	Partial<Pick<PageDefinition['data'], 'isLandingPage' | 'isMobileLandingPage' | 'isPopup'>>

/**
 * Checks if the page is the master page.
 */
export const isMasterPage = (pageData: PageData): boolean => pageData.id === MASTER_PAGE_ID

/**
 * Checks if the page is a popup.
 */
export const isPopup = (pageData?: PageData): boolean => !!pageData?.isPopup

/**
 * Checks if the page is routable (not master page and not popup).
 */
export const isRoutablePage = (pageData: PageData): boolean => !isMasterPage(pageData) && !isPopup(pageData)

/**
 * Checks if the page is a landing page.
 * Returns the mobile landing page status if in mobile view, otherwise desktop.
 */
export const isLandingPage = (pageData: PageData, isMobileView: boolean): boolean =>
	isMobileView ? !!pageData.isMobileLandingPage : !!pageData.isLandingPage

export const refetchDynamicPageData = async (
	currentRouteInfo: ICurrentRouteInfo,
	dynamicPagesRoutingMiddleware?: IRoutingMiddleware
) => {
	const routeInfo = currentRouteInfo.getCurrentRouteInfo()

	if (routeInfo?.dynamicRouteData && dynamicPagesRoutingMiddleware) {
		// Call the handler to get the new dynamicRouteData after update
		const dynamicRouteInfo = await dynamicPagesRoutingMiddleware.handle({
			...routeInfo,
			// This needs to be undefined in order for the handler to trigger again
			pageId: undefined,
		} as Partial<CandidateRouteInfo>)
		if (dynamicRouteInfo?.dynamicRouteData) {
			// Update it on currentRouteInfo symbol
			currentRouteInfo.updateCurrentDynamicRouteInfo(dynamicRouteInfo.dynamicRouteData)
		}
	}
}

export const DEFAULT_PAGE_ID: string = 'c1dmp'
